import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, Search as SearchIcon, Users, Globe2, BookOpen, FlaskConical, GraduationCap, Check } from "lucide-react";
import Seo from "@/components/Seo";
import ShareBar from "@/components/ShareBar";

const SITE = "https://mykind.app";

export const FEATURES = {
  search: {
    icon: SearchIcon, title: "Ask & Search", tagline: "Clean, ad-free answers that pay you back",
    image: "/f/search.jpg", tryPath: "/search", tryLabel: "Ask or Search",
    desc: "Ask anything and get clean, ad-free answers powered by AI — then earn credits for the value your questions and insights create. No tracking, no manipulation, no noise.",
    steps: ["Type any question or search", "Get a clear, sourced answer", "Earn credits as you go"],
    keywords: "ai search, ad-free search, ask ai, private search, learn and earn",
  },
  social: {
    icon: Users, title: "The Kind Social", tagline: "Your people, your data — kindness first",
    image: "/f/social.jpg", tryPath: "/personal", tryLabel: "Open my space",
    desc: "A kinder social home: your profile, communities and network, health, stories and a personal assistant for everything — all owned by you. A gentler alternative to conventional social media.",
    steps: ["Build your Kind profile", "Connect with your kind of people", "Own your data & earn from it"],
    keywords: "kindness social media, conscious community, connect, own your data, kind social",
  },
  community: {
    icon: Globe2, title: "Global Community", tagline: "Learn, travel & own — together",
    image: "/f/community.jpg", tryPath: "/personal?tab=community", tryLabel: "Explore the community",
    desc: "A decentralised, democratic ecosystem where every member learns, earns and belongs — community travel, events, food, philanthropy and shared ownership.",
    steps: ["Join a community of kindred souls", "Share rides, events, meals & giving", "Grow and own it together"],
    keywords: "community, philanthropy, belonging, community travel, events, giving back",
  },
  book: {
    icon: BookOpen, title: "Book Interpretation", tagline: "Any book or document, explained for you",
    image: "/f/book.jpg", tryPath: "/personal?tab=books", tryLabel: "Interpret a book",
    desc: "Upload any book or document and My Kind interprets it for you — clear summaries, key ideas, and personalised meaning aligned to your goals, in your own language.",
    steps: ["Upload a book or document", "Get a clear, personalised interpretation", "Save & share the wisdom"],
    keywords: "book summary, document interpretation, ai reading, study help, wisdom",
  },
  "product-safety": {
    icon: FlaskConical, title: "Product Safety", tagline: "Scan a label — flag toxins, find clean swaps",
    image: "/f/product-safety.jpg", tryPath: "/personal?tab=receipts", tryLabel: "Try a free scan",
    desc: "Scan any grocery, shopping or restaurant receipt or ingredient label. My Kind flags harmful ingredients and toxic chemicals (parabens, phthalates, SLS, PFAS, synthetic fragrance & more), tallies calories & macros, grades your purchase and suggests cleaner, non-toxic swaps. Privacy-first — names & card numbers are never stored.",
    steps: ["Upload a photo of a label or receipt", "Run the molecular scan for harmful ingredients", "See your grade, clean swaps & share"],
    keywords: "product safety scanner, harmful ingredients, non-toxic, clean swaps, molecular scan, PFAS parabens",
  },
  courses: {
    icon: GraduationCap, title: "VishwaGuru Courses", tagline: "Learn anything. Earn as you teach.",
    image: "/f/courses.jpg", tryPath: "/learn", tryLabel: "Explore courses",
    desc: "A decentralised distance-learning university: study courses on Yoga, Advaita, AI, Digital Transformation, wellbeing and more — pass exams, earn shareable certificates, and earn credits (teachers keep 75%) when others learn from your courses.",
    steps: ["Pick or create a course", "Study daily modules & pass the exam", "Earn a certificate & credits"],
    keywords: "online courses, yoga, advaita, ai course, digital transformation, learn and earn, free certificate",
  },
  galactopedia: {
    icon: BookOpen, title: "Galactopedia", tagline: "The collective brain of the community",
    image: "/f/galactopedia.jpg", tryPath: "/galactopedia", tryLabel: "Open Galactopedia",
    desc: "The living, ever-growing knowledge base of My Kind — a collective brain built from the community's questions, answers and wisdom, in your language, owned by all of us.",
    steps: ["Ask or explore any topic", "Learn from the collective wisdom", "Contribute & grow the brain"],
    keywords: "galactopedia, collective intelligence, knowledge base, community wisdom, ask ai, encyclopedia",
  },
};

export default function FeaturePage() {
  const { slug } = useParams();
  const f = FEATURES[slug];
  if (!f) return <Navigate to="/" replace />;
  const Icon = f.icon;
  const shareUrl = `${SITE}/f/${slug}.html`;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-24 pb-20" data-testid={`feature-page-${slug}`}>
      <Seo title={f.title} description={f.desc} keywords={f.keywords} path={`/f/${slug}`} image={`${SITE}${f.image}`} />

      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand mb-4">
        <Icon size={15} /> My Kind · {f.title}
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="rounded-3xl overflow-hidden border border-brand/30 relative group">
        <img src={f.image} alt={f.title} className="w-full aspect-[16/10] sm:aspect-[16/9] object-cover" />
        <Link to={f.tryPath} className="absolute inset-0 grid place-items-center bg-black/10 hover:bg-black/25 transition-colors" data-testid="feature-play">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-brand text-black shadow-lg group-hover:scale-110 transition-transform"><PlayCircle size={34} /></span>
        </Link>
      </motion.div>

      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-8">{f.title}</h1>
      <p className="text-brand text-base mt-1">{f.tagline}</p>
      <p className="text-sec text-sm sm:text-base mt-4 max-w-3xl leading-relaxed">{f.desc}</p>

      {/* animated explainer steps */}
      <div className="grid sm:grid-cols-3 gap-4 mt-9" data-testid="feature-steps">
        {f.steps.map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
            className="rud-card rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute top-3 right-4 font-serif text-5xl text-brand/15 leading-none select-none">{i + 1}</div>
            <div className="grid place-items-center w-10 h-10 rounded-lg bg-brand/15 text-brand"><Check size={20} /></div>
            <p className="text-[var(--rud-text)] text-base mt-4 leading-relaxed">{s}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-9 flex flex-wrap items-center gap-3">
        <Link to={f.tryPath} data-testid="feature-try-cta" className="btn-primary inline-flex items-center gap-2 rounded-full h-12 px-8 text-base">
          {f.tryLabel} <ArrowRight size={18} />
        </Link>
      </div>

      <div className="mt-10 border-t border-rud pt-7">
        <div className="text-xs uppercase tracking-widest text-sec mb-3">Share this</div>
        <ShareBar url={shareUrl} title={`My Kind · ${f.title}`} text={`${f.title} — ${f.tagline} on My Kind`} />
      </div>
    </div>
  );
}
